import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
// perf-optimized
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { CartProvider } from './lib/cart';
import { TableOrderProvider } from './lib/table-context';
import { KitchenPauseProvider } from './lib/kitchen-pause-context';
import { LanguageProvider } from './lib/i18n';
import { KitchenPausedBanner } from './components/restaurant/KitchenPausedBanner';
import { getSubdomainSlug } from './lib/subdomain';
import { ScrollToTop } from './components/ui/ScrollToTop';
// Eager Customer Pages (Critical Path)
import { HomePage } from './pages/HomePage';
import { RestaurantPage } from './pages/RestaurantPage';
import { PublicMenuPage } from './pages/PublicMenuPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { RequireAdminAuth } from './components/admin/RequireAdminAuth';
import { RequirePlatformAdmin } from './components/admin/RequirePlatformAdmin';
// Lazy Customer Pages
const RestaurantListingPage = lazy(() => import('./pages/RestaurantListingPage').then(m => ({
  default: m.RestaurantListingPage
})));
const MenuPage = lazy(() => import('./pages/MenuPage').then(m => ({
  default: m.MenuPage
})));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage').then(m => ({
  default: m.OrderConfirmationPage
})));
const TrackingPage = lazy(() => import('./pages/TrackingPage').then(m => ({
  default: m.TrackingPage
})));
const TablePage = lazy(() => import('./pages/TablePage').then(m => ({
  default: m.TablePage
})));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({
  default: m.OnboardingPage
})));
const TermsRestaurantsPage = lazy(() => import('./pages/TermsRestaurantsPage').then(m => ({
  default: m.TermsRestaurantsPage
})));
const NatuCafePage = lazy(() => import('./pages/NatuCafePage').then(m => ({
  default: m.NatuCafePage
})));
const InvoicePage = lazy(() => import('./pages/InvoicePage').then(m => ({
  default: m.InvoicePage
})));
const TicketPage = lazy(() => import('./pages/TicketPage').then(m => ({
  default: m.TicketPage
})));
// Lazy Admin Pages
const LoginPage = lazy(() => import('./pages/admin/LoginPage').then(m => ({
  default: m.LoginPage
})));
const NoizaMetricsPage = lazy(() => import('./pages/admin/NoizaMetricsPage').then(m => ({
  default: m.NoizaMetricsPage
})));
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage').then(m => ({
  default: m.DashboardPage
})));
const KDSPage = lazy(() => import('./pages/admin/KDSPage').then(m => ({
  default: m.KDSPage
})));
const InventoryPage = lazy(() => import('./pages/admin/InventoryPage').then(m => ({
  default: m.InventoryPage
})));
const MetricsPage = lazy(() => import('./pages/admin/MetricsPage').then(m => ({
  default: m.MetricsPage
})));
const MenuManagementPage = lazy(() => import('./pages/admin/MenuManagementPage').then(m => ({
  default: m.MenuManagementPage
})));
const ImportPage = lazy(() => import('./pages/admin/ImportPage').then(m => ({
  default: m.ImportPage
})));
const WaiterPOSPage = lazy(() => import('./pages/admin/WaiterPOSPage').then(m => ({
  default: m.WaiterPOSPage
})));
const StationSelectPage = lazy(() => import('./pages/admin/StationSelectPage').then(m => ({
  default: m.StationSelectPage
})));
const CajaOverviewPage = lazy(() => import('./pages/admin/CajaOverviewPage').then(m => ({
  default: m.CajaOverviewPage
})));
const POSCajaPage = lazy(() => import('./pages/admin/POSCajaPage').then(m => ({
  default: m.POSCajaPage
})));
const CancelledOrdersPage = lazy(() => import('./pages/admin/CancelledOrdersPage').then(m => ({
  default: m.CancelledOrdersPage
})));
const TableManagementPage = lazy(() => import('./pages/admin/TableManagementPage').then(m => ({
  default: m.TableManagementPage
})));
const TableQRPage = lazy(() => import('./pages/admin/TableQRPage').then(m => ({
  default: m.TableQRPage
})));
const MenuQRPage = lazy(() => import('./pages/admin/MenuQRPage').then(m => ({
  default: m.MenuQRPage
})));
const LoyaltyDashboardPage = lazy(() => import('./pages/admin/LoyaltyDashboardPage').then(m => ({
  default: m.LoyaltyDashboardPage
})));
const LoyaltyRedemptionPage = lazy(() => import('./pages/admin/LoyaltyRedemptionPage').then(m => ({
  default: m.LoyaltyRedemptionPage
})));
const InvoiceRequestsPage = lazy(() => import('./pages/admin/InvoiceRequestsPage').then(m => ({
  default: m.InvoiceRequestsPage
})));
const CreateRestaurantPage = lazy(() => import('./pages/admin/CreateRestaurantPage').then(m => ({
  default: m.CreateRestaurantPage
})));
const DriversListPage = lazy(() => import('./pages/admin/DriversListPage').then(m => ({
  default: m.DriversListPage
})));
const CreateDriverPage = lazy(() => import('./pages/admin/CreateDriverPage').then(m => ({
  default: m.CreateDriverPage
})));
const VehiclesListPage = lazy(() => import('./pages/admin/VehiclesListPage').then(m => ({
  default: m.VehiclesListPage
})));
const CreateVehiclePage = lazy(() => import('./pages/admin/CreateVehiclePage').then(m => ({
  default: m.CreateVehiclePage
})));
const AssignmentsPage = lazy(() => import('./pages/admin/AssignmentsPage').then(m => ({
  default: m.AssignmentsPage
})));
const UsersAdminPage = lazy(() => import('./pages/admin/UsersAdminPage').then(m => ({
  default: m.UsersAdminPage
})));
const EnrollmentPage = lazy(() => import('./pages/admin/EnrollmentPage').then(m => ({
  default: m.EnrollmentPage
})));
const EnrollmentRestaurantPage = lazy(() => import('./pages/admin/EnrollmentRestaurantPage').then(m => ({
  default: m.EnrollmentRestaurantPage
})));
const EnrollmentMenuUpdatePage = lazy(() => import('./pages/admin/EnrollmentMenuUpdatePage').then(m => ({
  default: m.EnrollmentMenuUpdatePage
})));
const EnrollmentPlatformAdminPage = lazy(() => import('./pages/admin/EnrollmentPlatformAdminPage').then(m => ({
  default: m.EnrollmentPlatformAdminPage
})));
const EnrollmentRestaurantAdminPage = lazy(() => import('./pages/admin/EnrollmentRestaurantAdminPage').then(m => ({
  default: m.EnrollmentRestaurantAdminPage
})));
const EnrollmentMenuImagesPage = lazy(() => import('./pages/admin/EnrollmentMenuImagesPage').then(m => ({
  default: m.EnrollmentMenuImagesPage
})));
const ReceiptGeneratorPage = lazy(() => import('./pages/admin/ReceiptGeneratorPage').then(m => ({
  default: m.ReceiptGeneratorPage
})));
const GlobalAdminLoginPage = lazy(() => import('./pages/admin/GlobalAdminLoginPage').then(m => ({
  default: m.GlobalAdminLoginPage
})));
// Lazy Driver Pages
const DriverLoginPage = lazy(() => import('./pages/driver/DriverLoginPage').then(m => ({
  default: m.DriverLoginPage
})));
const DriverAppPage = lazy(() => import('./pages/driver/DriverAppPage').then(m => ({
  default: m.DriverAppPage
})));
// Tenant Access
import { TenantProvider } from './lib/tenant-context';
// ─── MAINTENANCE GATE ───
// Slugs under maintenance — only accessible with ?dev=noiza2026
const MAINTENANCE_SLUGS = ['noiza.restaurant'];
function MaintenancePage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const handleUnlock = () => {
    if (code === 'noiza2026') {
      sessionStorage.setItem('noiza_dev_access', 'true');
      window.location.reload();
    } else {
      setError(true);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-0" data-mp="2gfc3vrcoy7s9">
      <div className="max-w-md w-full text-center" data-id="element-1" data-mp="2kge86mlgta13">
        <div className="w-16 h-16 mx-auto mb-6 bg-[#141414] rounded-2xl flex items-center justify-center border border-white/10" data-id="element-2" data-mp="2khyz828kmau7">
          <span className="text-2xl" data-id="element-3" data-mp="2vwk6kh45f84f">🔧</span>
        </div>
        <h1 className="text-2xl font-black text-white mb-2" data-id="element-4" data-mp="2vx28nudhsgk9">
          En Mantenimiento
        </h1>
        <p className="text-white/40 text-sm mb-8" data-id="element-5" data-mp="2qvcz6j5x4aya">
          Este restaurante está temporalmente en modo de pruebas. Volveremos
          pronto.
        </p>
        <div className="space-y-3" data-id="element-6" data-mp="2hwrh0p4cyxy1">
          <input type="password" placeholder="Código de acceso" value={code} onChange={e => {
          setCode(e.target.value);
          setError(false);
        }} onKeyDown={e => e.key === 'Enter' && handleUnlock()} className="w-full px-4 py-3 rounded-xl bg-[#141414] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#006847] focus:border-transparent text-center" data-id="element-7" data-mp="2hxu7ivxrqydf" />
          {error && <p className="text-[#CE1126] text-xs" data-id="element-8" data-mp="9owfofujaeo4">Código incorrecto</p>}
          <button onClick={handleUnlock} className="w-full py-3 rounded-xl bg-[#006847] text-white font-bold text-sm hover:bg-[#005a3d] transition-colors" data-id="element-9" data-mp="9omnqmpxhxfs">
            Acceder
          </button>
        </div>
      </div>
    </div>;
}
/** Routes Capricho de Canela to PublicMenuPage (premium theme), others to RestaurantPage */
function SmartRestaurantPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Maintenance gate: check URL param or session
  if (slug && MAINTENANCE_SLUGS.includes(slug)) {
    const urlParams = new URLSearchParams(window.location.search);
    const hasDevParam = urlParams.get('dev') === 'noiza2026';
    const hasSessionAccess = sessionStorage.getItem('noiza_dev_access') === 'true';
    if (hasDevParam && !hasSessionAccess) {
      sessionStorage.setItem('noiza_dev_access', 'true');
    }
    if (!hasDevParam && !hasSessionAccess) {
      return <MaintenancePage data-id="element-10" data-mp="38cjx710748gh" />;
    }
  }
  if (slug === 'capricho-de-canela') {
    return <PublicMenuPage data-id="element-11" data-mp="2f6vdeuvn1200" />;
  }
  return <RestaurantPage data-id="element-12" data-mp="2k5ybfvd7a7yh" />;
}
/** Redirects /admin/:slug → /admin/:slug/operacion (station select) */
function AdminRedirect() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  return <Navigate to={`/admin/${slug}/operacion`} replace data-id="element-13" data-mp="2p3gnvvqff5b8" />;
}
/**
 * Subdomain-aware admin redirect.
 * When on a tenant subdomain, /admin → /admin/dashboard
 */
function SubdomainAdminRedirect({
  slug
}: {
  slug: string;
}) {
  return <Navigate to="/admin/operacion" replace data-id="element-14" data-mp="21f75l3sf8zou" />;
}
/**
 * Wraps TenantProvider to inject the subdomain slug
 * when there's no :slug in the path params.
 */
function SubdomainTenantProvider({
  children,
  slug
}: {
  children: React.ReactNode;
  slug: string;
}) {
  // We pass the slug through a context-compatible wrapper
  // TenantProvider reads from useParams, but we need to override it.
  // Instead, we'll use the updated TenantProvider that accepts an optional slug prop.
  return <TenantProvider subdomainSlug={slug} data-id="element-15" data-mp="3vzifnso9fjk6">{children}</TenantProvider>;
}
/**
 * Wrapper for marketplace admin routes that need TenantProvider.
 * TenantProvider reads :slug from useParams and handles auth + access.
 */
function TenantAdminRoute({
  children
}: {
  children: React.ReactNode;
}) {
  return <TenantProvider data-id="element-16" data-mp="3hzpusdzezxk4">{children}</TenantProvider>;
}
/**
 * Main routing component.
 * Detects subdomain and renders appropriate routes.
 */
function AppRoutes() {
  const subdomainSlug = getSubdomainSlug();
  // ─── SUBDOMAIN MODE ───
  // When accessed via {slug}.noiza.estrategiasdigitalesaz.com
  if (subdomainSlug) {
    return <SubdomainTenantProvider slug={subdomainSlug} data-id="element-17" data-mp="347jfsv7zzwm9">
        <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-19" data-mp="2za12ksmy6v8g">
              <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-20" data-mp="2uaoi2ejju091"></div>
            </div>} data-id="element-18" data-mp="346884kbfm4pv">
          <Routes data-id="element-21" data-mp="2pbtxp85xo3mt">
            {/* Customer Routes */}
            <Route path="/" element={<SmartRestaurantPage data-id="element-23" data-mp="2flv5p59f7dq8" />} data-id="element-22" data-mp="2fn6jlxwky1v2" />
            <Route path="/menu" element={<MenuPage data-id="element-25" data-mp="2kn2gld6v0t14" />} data-id="element-24" data-mp="2kmj78kv9zcyp" />
            <Route path="/cart" element={<CartPage data-id="element-27" data-mp="2kkg7h94bm5gs" />} data-id="element-26" data-mp="2knc8j6bgta9g" />
            <Route path="/checkout" element={<CheckoutPage data-id="element-29" data-mp="26nt7exh2mz6n" />} data-id="element-28" data-mp="26n1e5tay5djj" />
            <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-31" data-mp="2ap4qek5fwm5v" />} data-id="element-30" data-mp="26oumre54e7u6" />
            <Route path="/tracking/:orderId" element={<TrackingPage data-id="element-33" data-mp="2yez5z9h58ott" />} data-id="element-32" data-mp="2ydxoon9jb63n" />
            <Route path="/t/:tableId" element={<TablePage data-id="element-35" data-mp="33cr5aok4bd81" />} data-id="element-34" data-mp="2yg0q03g50lno" />
            {/* QR code routes — match QR URL format from TableQRPage */}
            <Route path="/mesa/:tableNumber/:token" element={<TablePage data-id="element-37" data-mp="2pldnpg5rspfn" />} data-id="element-36" data-mp="2pm5kuvf0mv81" />
            <Route path="/mesa/:tableNumber" element={<TablePage data-id="element-39" data-mp="2tm5yiduaj2e1" />} data-id="element-38" data-mp="2tn7m1hshthcn" />

            {/* Admin Routes */}
            <Route path="/admin" element={<SubdomainAdminRedirect slug={subdomainSlug} data-id="element-41" data-mp="3hb8qofmjwdms" />} data-id="element-40" data-mp="3hctku44yduk4" />
            <Route path="/admin/login" element={<LoginPage data-id="element-43" data-mp="3m4texfycdt8s" />} data-id="element-42" data-mp="3m4jz2l70h2gt" />
            <Route path="/admin/operacion" element={<RequireAdminAuth data-id="element-45" data-mp="1am6p5j7yf83t">
                  <StationSelectPage data-id="element-46" data-mp="15pzpu9a2cuuu" />
                </RequireAdminAuth>} data-id="element-44" data-mp="1aojfokrs2qsh" />
            <Route path="/admin/dashboard" element={<RequireAdminAuth data-id="element-48" data-mp="1ffje5ugjpgx3">
                  <DashboardPage data-id="element-49" data-mp="1fgko1vltrxd8" />
                </RequireAdminAuth>} data-id="element-47" data-mp="1kew10f7f2nzo" />
            <Route path="/admin/kds" element={<RequireAdminAuth data-id="element-51" data-mp="mr3xl3hrmqjn">
                  <KDSPage data-id="element-52" data-mp="ms5cxk5tdz76" />
                </RequireAdminAuth>} data-id="element-50" data-mp="rqqfvrcgawzb" />
            <Route path="/admin/caja" element={<RequireAdminAuth data-id="element-54" data-mp="woie133ovfby">
                  <CajaOverviewPage data-id="element-55" data-mp="wngqw09ef2dv" />
                </RequireAdminAuth>} data-id="element-53" data-mp="11omks1esovoz" />
            <Route path="/admin/pos-caja" element={<RequireAdminAuth data-id="element-57" data-mp="2835fsx67qbhb">
                  <POSCajaPage data-id="element-58" data-mp="2869pub6d01fw" />
                </RequireAdminAuth>} data-id="element-56" data-mp="2d16y3fvte6rq" />
            <Route path="/admin/cancelled" element={<RequireAdminAuth data-id="element-60" data-mp="mm6dowu6q1ul">
                  <CancelledOrdersPage data-id="element-61" data-mp="qnrkq1k1flwv" />
                </RequireAdminAuth>} data-id="element-59" data-mp="howbeglp5vpc" />
            <Route path="/admin/inventory" element={<RequireAdminAuth data-id="element-63" data-mp="vk8b72epmcd4">
                  <InventoryPage data-id="element-64" data-mp="10mh2lubxp6d4" />
                </RequireAdminAuth>} data-id="element-62" data-mp="vmcna7mrihoh" />
            <Route path="/admin/metrics" element={<RequireAdminAuth data-id="element-66" data-mp="1abqxtj22r55g">
                  <MetricsPage data-id="element-67" data-mp="1acs99on05tnp" />
                </RequireAdminAuth>} data-id="element-65" data-mp="15jz4j0rjalbm" />
            <Route path="/admin/menu" element={<RequireAdminAuth data-id="element-69" data-mp="1jcp11z4ov8mz">
                  <MenuManagementPage data-id="element-70" data-mp="1jdqgefsqmhai" />
                </RequireAdminAuth>} data-id="element-68" data-mp="1ec11uq6b3lho" />
            <Route path="/admin/import" element={<RequireAdminAuth data-id="element-72" data-mp="1yg8exszqdvp9">
                  <ImportPage data-id="element-73" data-mp="1yd4103wgrlle" />
                </RequireAdminAuth>} data-id="element-71" data-mp="1obrusnf7xsfn" />
            <Route path="/admin/waiter" element={<RequireAdminAuth data-id="element-75" data-mp="1ohj42k695bii">
                  <WaiterPOSPage data-id="element-76" data-mp="1oiu4qb4ei35e" />
                </RequireAdminAuth>} data-id="element-74" data-mp="1tesspxlthj5a" />
            <Route path="/admin/tables" element={<RequireAdminAuth data-id="element-78" data-mp="2h4wr9qk5espf">
                  <TableManagementPage data-id="element-79" data-mp="2c6vchhtr9fjr" />
                </RequireAdminAuth>} data-id="element-77" data-mp="1khs7fzu1o162" />
            <Route path="/admin/qr" element={<RequireAdminAuth data-id="element-81" data-mp="27dip1b0tbesl">
                  <TableQRPage data-id="element-82" data-mp="23f35jh3xwojx" />
                </RequireAdminAuth>} data-id="element-80" data-mp="27fn2ajklpq5j" />
            <Route path="/admin/menu-qr" element={<RequireAdminAuth data-id="element-84" data-mp="2v5pqgoleabyv">
                  <MenuQRPage data-id="element-85" data-mp="2v4el4kcaww5n" />
                </RequireAdminAuth>} data-id="element-83" data-mp="2zwpojx69302y" />
            <Route path="/admin/loyalty" element={<RequireAdminAuth data-id="element-87" data-mp="y7jpgvkpddxf">
                  <LoyaltyDashboardPage data-id="element-88" data-mp="y6h0wuaurnko" />
                </RequireAdminAuth>} data-id="element-86" data-mp="t8yq0yd9yya1" />
            <Route path="/admin/loyalty/redemption" element={<RequireAdminAuth data-id="element-90" data-mp="ofw339306n2t">
                  <LoyaltyRedemptionPage data-id="element-91" data-mp="oeuiacw6qm7w" />
                </RequireAdminAuth>} data-id="element-89" data-mp="jhkut2scwkmp" />
            <Route path="/admin/invoice-requests" element={<RequireAdminAuth data-id="element-93" data-mp="fe63vh2vqjuh">
                  <InvoiceRequestsPage data-id="element-94" data-mp="k1jttmc8rxn" />
                </RequireAdminAuth>} data-id="element-92" data-mp="fgiu0hisk0im" />

            {/* Platform Admin Routes (Subdomain) */}
            <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-96" data-mp="1uv6iskmhcg6n">
                  <ReceiptGeneratorPage data-id="element-97" data-mp="1zwvv03lfuzwb" />
                </RequirePlatformAdmin>} data-id="element-95" data-mp="5kfkgqlm0zln" />

            {/* Catch-all */}
            <Route path="/factura/:paymentId" element={<InvoicePage data-id="element-99" data-mp="3ffbqe1igggh6" />} data-id="element-98" data-mp="3key040z2rel8" />
            <Route path="/ticket/:orderNumber" element={<TicketPage data-id="element-101" data-mp="3fd8ook7xwywm" />} data-id="element-100" data-mp="3fg3bgiefevt7" />
            <Route path="*" element={<Navigate to="/" replace data-id="element-103" data-mp="3u4ho1l1n9ymy" />} data-id="element-102" data-mp="3u3y2zvgp6u4p" />
          </Routes>
        </Suspense>
      </SubdomainTenantProvider>;
  }
  // ─── MARKETPLACE MODE ───
  // When accessed via noizacr.com
  return <Suspense fallback={<div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center" data-id="element-105" data-mp="6xxfbx1t7m29">
          <div className="w-8 h-8 border-4 border-[#8B5E3C] border-t-transparent rounded-full animate-spin" data-id="element-106" data-mp="2x53cw1jz32a"></div>
        </div>} data-id="element-104" data-mp="6yqo13kbwln1">
      <Routes data-id="element-107" data-mp="gvbrhkgp9oor">
        {/* Marketplace Routes */}
        <Route path="/" element={<HomePage data-id="element-109" data-mp="bzeb5e5fsibz" />} data-id="element-108" data-mp="bzmqrdsy21qv" />
        <Route path="/restaurants" element={<RestaurantListingPage data-id="element-111" data-mp="2io9fn2zj7t3d" />} data-id="element-110" data-mp="bws73ohr8kyi" />
        <Route path="/terminos-restaurantes" element={<TermsRestaurantsPage data-id="element-113" data-mp="2dqrh86j54w9m" />} data-id="element-112" data-mp="2ipb366xqi81z" />
        <Route path="/natucafe" element={<NatuCafePage data-id="element-115" data-mp="1rx26ps0sf9l2" />} data-id="element-114" data-mp="2ds2jg1wxtvym" />
        <Route path="/onboarding" element={<OnboardingPage data-id="element-117" data-mp="1rwisalacj3bs" />} data-id="element-116" data-mp="1rvr6u3agqlz8" />

        {/* Shared customer routes (work without slug for legacy/direct links) */}
        <Route path="/cart" element={<CartPage data-id="element-119" data-mp="21m2adoq8it5l" />} data-id="element-118" data-mp="1wtixktzdg21u" />
        <Route path="/checkout" element={<CheckoutPage data-id="element-121" data-mp="21oosujenyt2l" />} data-id="element-120" data-mp="21nn79l00uo6m" />
        <Route path="/order-confirmation" element={<OrderConfirmationPage data-id="element-123" data-mp="26kdk7zgj53nt" />} data-id="element-122" data-mp="26lf7d2atlglw" />

        {/* Restaurant Specific Routes */}
        <Route path="/r/:slug" element={<SmartRestaurantPage data-id="element-125" data-mp="18zhjsqhl2l33" />} data-id="element-124" data-mp="18ypzibtfs9s4" />
        <Route path="/r/:slug/menu" element={<MenuPage data-id="element-127" data-mp="1dy2k0pwu54rj" />} data-id="element-126" data-mp="1dx18kkbwqg9a" />
        <Route path="/r/:slug/cart" element={<CartPage data-id="element-129" data-mp="1iw41j6em4w0w" />} data-id="element-128" data-mp="1dxjbfzt2rsq6" />
        <Route path="/r/:slug/checkout" element={<CheckoutPage data-id="element-131" data-mp="1iujahqribv7s" />} data-id="element-130" data-mp="1ivkvamybrw2p" />
        <Route path="/r/:slug/order-confirmation" element={<OrderConfirmationPage data-id="element-133" data-mp="1nws0qfczwj67" />} data-id="element-132" data-mp="1iygsua69giqm" />
        <Route path="/r/:slug/tracking/:orderId" element={<TrackingPage data-id="element-135" data-mp="2uarc9pz472ie" />} data-id="element-134" data-mp="2ubj9144g76a9" />
        <Route path="/r/:slug/t/:tableId" element={<TablePage data-id="element-137" data-mp="2z9ks3p1vj5lq" />} data-id="element-136" data-mp="2udlx3im1ipcc" />
        {/* QR code routes — match QR URL format from TableQRPage */}
        <Route path="/r/:slug/mesa/:tableNumber/:token" element={<TablePage data-id="element-139" data-mp="inxq8km7dd59" />} data-id="element-138" data-mp="nna7mpub1s0g" />
        <Route path="/r/:slug/mesa/:tableNumber" element={<TablePage data-id="element-141" data-mp="iqa2ch5a4tae" />} data-id="element-140" data-mp="ioyy6g7x9jir" />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<GlobalAdminLoginPage data-id="element-143" data-mp="dv4g1h7ytcls" />} data-id="element-142" data-mp="dw5wk17r2raw" />
        <Route path="/admin/metrics" element={<RequirePlatformAdmin data-id="element-145" data-mp="8wa0co5ao45w">
              <NoizaMetricsPage data-id="element-146" data-mp="3z9v2fg9r5fu" />
            </RequirePlatformAdmin>} data-id="element-144" data-mp="8ymfyunky2fs" />
        <Route path="/admin/restaurants/new" element={<RequirePlatformAdmin data-id="element-148" data-mp="3w56oahji6czy">
              <CreateRestaurantPage data-id="element-149" data-mp="3r2xujiyt36ws" />
            </RequirePlatformAdmin>} data-id="element-147" data-mp="3w32ejiyxajrr" />
        <Route path="/admin/drivers" element={<RequirePlatformAdmin data-id="element-151" data-mp="3m6zb89sc4f38">
              <DriversListPage data-id="element-152" data-mp="1pcmfi4iijq2i" />
            </RequirePlatformAdmin>} data-id="element-150" data-mp="3m55zwh6s16ow" />
        <Route path="/admin/drivers/new" element={<RequirePlatformAdmin data-id="element-154" data-mp="1kbgm04h13yrx">
              <CreateDriverPage data-id="element-155" data-mp="2c7lyrcocp74j" />
            </RequirePlatformAdmin>} data-id="element-153" data-mp="1kcrnfxn04ufv" />
        <Route path="/admin/vehicles" element={<RequirePlatformAdmin data-id="element-157" data-mp="27alt32vey6dy">
              <VehiclesListPage data-id="element-158" data-mp="22grc477705ik" />
            </RequirePlatformAdmin>} data-id="element-156" data-mp="277purdfnlfoz" />
        <Route path="/admin/vehicles/new" element={<RequirePlatformAdmin data-id="element-160" data-mp="1xglqp8rinf8x">
              <CreateVehiclePage data-id="element-161" data-mp="1th4m0hjwym4t" />
            </RequirePlatformAdmin>} data-id="element-159" data-mp="1xipwxxcw0qcd" />
        <Route path="/admin/assignments" element={<RequirePlatformAdmin data-id="element-163" data-mp="1ok4d7yvoj5a0">
              <AssignmentsPage data-id="element-164" data-mp="1jhvjh0azfz6u" />
            </RequirePlatformAdmin>} data-id="element-162" data-mp="1oh8guezhcono" />
        <Route path="/admin/users" element={<RequirePlatformAdmin data-id="element-166" data-mp="1elojrp96jjxc">
              <UsersAdminPage data-id="element-167" data-mp="3dx7ua8a4t094" />
            </RequirePlatformAdmin>} data-id="element-165" data-mp="1eju34n4xydz2" />
        <Route path="/admin/enrollment" element={<RequirePlatformAdmin data-id="element-169" data-mp="38vihoo79geix">
              <EnrollmentPage data-id="element-170" data-mp="3hf5c3qg7e3nc" />
            </RequirePlatformAdmin>} data-id="element-168" data-mp="38xuxovtgketc" />
        <Route path="/admin/enrollment/restaurant" element={<RequirePlatformAdmin data-id="element-172" data-mp="3ldumda5y352r">
              <EnrollmentRestaurantPage data-id="element-173" data-mp="3q6nrhz5bsffd" />
            </RequirePlatformAdmin>} data-id="element-171" data-mp="3lg7bc7a4efpb" />
        <Route path="/admin/enrollment/menu-update" element={<RequirePlatformAdmin data-id="element-175" data-mp="2yg150n882jyb">
              <EnrollmentMenuUpdatePage data-id="element-176" data-mp="2ygsztvtzwdnj" />
            </RequirePlatformAdmin>} data-id="element-174" data-mp="3v6jvhye0w6ux" />
        <Route path="/admin/enrollment/platform-admin" element={<RequirePlatformAdmin data-id="element-178" data-mp="37g6cexdf1ich">
              <EnrollmentPlatformAdminPage data-id="element-179" data-mp="37f4p9uj4l5ee" />
            </RequirePlatformAdmin>} data-id="element-177" data-mp="32ie8t63es7sg" />
        <Route path="/admin/enrollment/restaurant-admin" element={<RequirePlatformAdmin data-id="element-181" data-mp="mpp3xf2oe8w5">
              <EnrollmentRestaurantAdminPage data-id="element-182" data-mp="msl4zc9tldot" />
            </RequirePlatformAdmin>} data-id="element-180" data-mp="3cgu31hxqfx66" />
        <Route path="/admin/enrollment/menu-images" element={<RequirePlatformAdmin data-id="element-184" data-mp="238zfx7rf66g">
              <EnrollmentMenuImagesPage data-id="element-185" data-mp="3t7z03n3rxts0" />
            </RequirePlatformAdmin>} data-id="element-183" data-mp="rr4mn0hds3bd" />
        <Route path="/admin/enrollment/receipts" element={<RequirePlatformAdmin data-id="element-187" data-mp="70988fvzun19">
              <ReceiptGeneratorPage data-id="element-188" data-mp="6zzd6dw3dpop" />
            </RequirePlatformAdmin>} data-id="element-186" data-mp="bz2djl14n7qc" />
        <Route path="/admin/enrollment/metrics" element={<RequirePlatformAdmin data-id="element-190" data-mp="3ah7v0xqtkpjf">
              <NoizaMetricsPage data-id="element-191" data-mp="3ai0xhmiqwsvr" />
            </RequirePlatformAdmin>} data-id="element-189" data-mp="3ff9e3io8wouw" />

        <Route path="/admin/:slug" element={<AdminRedirect data-id="element-193" data-mp="3p76seyajvwxu" />} data-id="element-192" data-mp="3p6448y4m48lm" />
        <Route path="/admin/:slug/login" element={<LoginPage data-id="element-195" data-mp="3ke44p6sgfhpk" />} data-id="element-194" data-mp="3kf5kfokf0sdm" />
        <Route path="/admin/:slug/operacion" element={<TenantAdminRoute data-id="element-197" data-mp="2wo9vr0b9adal">
              <StationSelectPage data-id="element-198" data-mp="2wngj5ipm8tkj" />
            </TenantAdminRoute>} data-id="element-196" data-mp="3kduepj7etajv" />
        <Route path="/admin/:slug/dashboard" element={<TenantAdminRoute data-id="element-200" data-mp="177e8gx7e1jf6">
              <DashboardPage data-id="element-201" data-mp="176cno10klik9" />
            </TenantAdminRoute>} data-id="element-199" data-mp="2rpyisgpnzmo5" />
        <Route path="/admin/:slug/kds" element={<TenantAdminRoute data-id="element-203" data-mp="xb185ghc87bg">
              <KDSPage data-id="element-204" data-mp="xbueigcdwut2" />
            </TenantAdminRoute>} data-id="element-202" data-mp="1c0qnqgq6gdv9" />
        <Route path="/admin/:slug/caja" element={<TenantAdminRoute data-id="element-206" data-mp="1pz6ug7o1i4de">
              <CajaOverviewPage data-id="element-207" data-mp="1q08gt8aiadaf" />
            </TenantAdminRoute>} data-id="element-205" data-mp="12ae5iv5u4wsa" />
        <Route path="/admin/:slug/pos-caja" element={<TenantAdminRoute data-id="element-209" data-mp="1gyipe2gmjecn">
              <POSCajaPage data-id="element-210" data-mp="1gx7c9c1agu8v" />
            </TenantAdminRoute>} data-id="element-208" data-mp="1uya0nvfragmy" />
        <Route path="/admin/:slug/cancelled" element={<TenantAdminRoute data-id="element-212" data-mp="5j3hi3mvn22r">
              <CancelledOrdersPage data-id="element-213" data-mp="5iu0h5jt8597" />
            </TenantAdminRoute>} data-id="element-211" data-mp="1kzaymvphxj8x" />
        <Route path="/admin/:slug/inventory" element={<TenantAdminRoute data-id="element-215" data-mp="prtjze4mfwyb">
              <InventoryPage data-id="element-216" data-mp="ps3b551eka5l" />
            </TenantAdminRoute>} data-id="element-214" data-mp="9l6vp00xkkrq" />
        <Route path="/admin/:slug/metrics" element={<TenantAdminRoute data-id="element-218" data-mp="kza7ylll19vm">
              <MetricsPage data-id="element-219" data-mp="g1h3py3uapxz" />
            </TenantAdminRoute>} data-id="element-217" data-mp="l1d6xv4pqdch" />
        <Route path="/admin/:slug/menu" element={<TenantAdminRoute data-id="element-221" data-mp="b4qkj1wqne8c">
              <MenuManagementPage data-id="element-222" data-mp="18qfneznm205e" />
            </TenantAdminRoute>} data-id="element-220" data-mp="b2xhdwlc3c53" />
        <Route path="/admin/:slug/import" element={<TenantAdminRoute data-id="element-224" data-mp="13se2saajdsrt">
              <ImportPage data-id="element-225" data-mp="yshye9xxfzbq" />
            </TenantAdminRoute>} data-id="element-223" data-mp="18obhyda2ct30" />
        <Route path="/admin/:slug/waiter" element={<TenantAdminRoute data-id="element-227" data-mp="uqd8nmalnmud">
              <WaiterPOSPage data-id="element-228" data-mp="ut95166su3gp" />
            </TenantAdminRoute>} data-id="element-226" data-mp="yukxrkkyz4t4" />
        <Route path="/admin/:slug/tables" element={<TenantAdminRoute data-id="element-230" data-mp="3fgcd3lsfrds8">
              <TableManagementPage data-id="element-231" data-mp="3fj8ddgrraeju" />
            </TenantAdminRoute>} data-id="element-229" data-mp="3jk21ob941vd9" />
        <Route path="/admin/:slug/qr" element={<TenantAdminRoute data-id="element-233" data-mp="1z1q3uij1ovey">
              <TableQRPage data-id="element-234" data-mp="1z0y5x11z6lli" />
            </TenantAdminRoute>} data-id="element-232" data-mp="1v0xt1kuiyigk" />
        <Route path="/admin/:slug/menu-qr" element={<TenantAdminRoute data-id="element-236" data-mp="28qrh40y7gyp8">
              <MenuQRPage data-id="element-237" data-mp="28s2jbwc05ye8" />
            </TenantAdminRoute>} data-id="element-235" data-mp="23yzq5pb50qyk" />
        <Route path="/admin/:slug/loyalty" element={<TenantAdminRoute data-id="element-239" data-mp="2io5yc2rydril">
              <LoyaltyDashboardPage data-id="element-240" data-mp="2incnaplyofun" />
            </TenantAdminRoute>} data-id="element-238" data-mp="2doj9sx6ocouh" />
        <Route path="/admin/:slug/loyalty/redemption" element={<TenantAdminRoute data-id="element-242" data-mp="2rpvlgwesy4ak">
              <LoyaltyRedemptionPage data-id="element-243" data-mp="2rou1g2ft67gp" />
            </TenantAdminRoute>} data-id="element-241" data-mp="2mrc1v2nol4ld" />
        <Route path="/admin/:slug/invoice-requests" element={<TenantAdminRoute data-id="element-245" data-mp="xaje5yev3ld1">
              <InvoiceRequestsPage data-id="element-246" data-mp="xbkzqwti7q90" />
            </TenantAdminRoute>} data-id="element-244" data-mp="sdjh2czzpsy2" />

        {/* Driver App Routes */}
        <Route path="/driver/login" element={<DriverLoginPage data-id="element-248" data-mp="1vnp0ncfbuh8t" />} data-id="element-247" data-mp="20o36cntghf3o" />
        <Route path="/driver/app" element={<DriverAppPage data-id="element-250" data-mp="1vpi6ipi48xfr" />} data-id="element-249" data-mp="1voq9da8vernd" />

        {/* Catch-all */}
        <Route path="/factura/:paymentId" element={<InvoicePage data-id="element-252" data-mp="29kka35zhqape" />} data-id="element-251" data-mp="29npysk47jakl" />
        <Route path="/ticket/:orderNumber" element={<TicketPage data-id="element-254" data-mp="24maewvdrna3s" />} data-id="element-253" data-mp="24nbyjo8ul4x4" />
        <Route path="*" element={<Navigate to="/" replace data-id="element-256" data-mp="2jkkuywd0cyvp" />} data-id="element-255" data-mp="24q6ileny8nq0" />
      </Routes>
    </Suspense>;
}
export function App() {
  // ─── Disable browser auto-translate for the whole app ───
  // Some mobile browsers (Chrome, Safari) auto-translate menu items like
  // "Frozen Capuccino" → "Capuchino helado". We set the document language
  // to Spanish and inject the Google notranslate meta so the menu names
  // are always shown exactly as stored. The translate="no" on the root
  // div below is inherited by every descendant (products, categories,
  // prices, modals, cart, checkout).
  useEffect(() => {
    try {
      document.documentElement.lang = 'es';
      const metaName = 'google';
      let meta = document.querySelector(`meta[name="${metaName}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = metaName;
        document.head.appendChild(meta);
      }
      meta.content = 'notranslate';
    } catch {
      /* silent */
    }
  }, []);
  return <LanguageProvider data-id="element-257" data-mp="2y7ji4f4u174m">
      <CartProvider data-id="element-258" data-mp="2y68golyv0bgo">
        <TableOrderProvider data-id="element-259" data-mp="2y5qb2yqb4kw3">
          <KitchenPauseProvider data-id="element-260" data-mp="32y9q804n7o30">
            <BrowserRouter data-id="element-261" data-mp="32zl10jwi9w3m">
              <ScrollToTop data-id="element-262" data-mp="32zun3wefjj41" />
              <KitchenPausedBanner data-id="element-263" data-mp="2o9n85qnmsi83" />
              <div translate="no" className="notranslate min-h-screen w-full flex flex-col bg-brand-offwhite font-sans text-gray-900" data-id="element-264" data-mp="2oaomc3zy1ku1">
                <AppRoutes data-id="element-265" data-mp="2f8p458d70api" />
              </div>
            </BrowserRouter>
          </KitchenPauseProvider>
        </TableOrderProvider>
      </CartProvider>
    </LanguageProvider>;
}